import React, { useEffect, useState, useRef, lazy, memo, createElement, Component } from 'react';
import { CheckIcon, StarIcon as StarIconLucide, ClockIcon, RocketIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import { CopiesAvailableCounter } from './CopiesAvailableCounter';
// Declare fbq function for TypeScript
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
    ClickMagick?: any;
  }
}
export const PricingSection = () => {
  const [hoveredBadge, setHoveredBadge] = useState(null);
  const [collectorBundleImageIndex, setCollectorBundleImageIndex] = useState(0);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);
  // Intersection Observer para disparar evento cuando la sección es visible
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    // Detectar si es mobile
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true;
          // Delay más largo en mobile para asegurar que los scripts estén cargados
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            // Crear y disparar un elemento .cta temporal (usando div en lugar de a para evitar scroll)
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            // Disparar múltiples tipos de eventos para máxima compatibilidad
            // Click event (desktop)
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            // Touch events (mobile)
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            console.log(`✅ ClickMagick .cta event triggered on pricing section view (${isMobile ? 'MOBILE' : 'DESKTOP'})`);
            // Limpiar después de 2 segundos
            setTimeout(() => {
              if (ctaTrigger.parentNode) {
                ctaTrigger.parentNode.removeChild(ctaTrigger);
              }
            }, 2000);
          }, delay);
        }
      });
    }, {
      // Threshold más bajo en mobile (10% vs 30% en desktop)
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  // Read fbclid from current page URL for passthrough to Shopify checkout
  const currentParams = new URLSearchParams(window.location.search);
  const landingFbclid = currentParams.get('fbclid');
  // Build checkout URL — simple string replace, no new URL() to preserve ?checkout format
  // If fbclid exists in landing URL → use real value
  // If not → keep {{fbclid}} so ClickMagick can handle it (like Taproot)
  const buildCheckoutUrl = (templateUrl: string) => {
    if (landingFbclid) {
      return templateUrl.replace('{{fbclid}}', landingFbclid);
    }
    return templateUrl;
  };
  const collectorBundleImages = ['https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
  const nextCollectorImage = () => {
    setCollectorBundleImageIndex(prev => prev === collectorBundleImages.length - 1 ? 0 : prev + 1);
  };
  const prevCollectorImage = () => {
    setCollectorBundleImageIndex(prev => prev === 0 ? collectorBundleImages.length - 1 : prev - 1);
  };
  const handleImageClick = (index: number) => {
    setEnlargedImage(collectorBundleImages[index]);
  };
  const handleMainImageClick = (imageUrl: string) => {
    setEnlargedImage(imageUrl);
  };
  const closeEnlargedImage = () => {
    setEnlargedImage(null);
  };
  // Flag to ensure InitiateCheckout fires only ONCE per session
  let icFired = false;
  // Fire AddToCart + InitiateCheckout (once) tracking — NO navigation, NO preventDefault
  // The <a href> handles navigation natively
  const fireTrackingEvents = (editionName: string, price: string) => {
    if (typeof window.fbq === 'function') {
      const value = parseFloat(price.replace('$', ''));
      window.fbq('track', 'AddToCart', {
        content_name: editionName,
        content_type: 'product',
        value,
        currency: 'USD'
      });
      // InitiateCheckout — only once per page session
      if (!icFired) {
        icFired = true;
        window.fbq('track', 'InitiateCheckout', {
          content_name: editionName,
          content_type: 'product',
          value,
          currency: 'USD'
        });
      }
    }
  };
  // Stop propagation helper - prevents inner clicks from triggering parent navigation
  const stopProp = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  // Mobile order: Complete Digital -> Premium -> Collector Bundle -> Digital Edition
  const editionsMobile = [{
    image: 'https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png',
    name: 'Complete Digital Edition',
    emoji: '🎄',
    originalPrice: '$114.97',
    currentPrice: '$59.97',
    savings: '$55.00',
    features: ['Back In Time - 40th Anniversary Collectors Edition (1985-2025) | Digital Edition', 'Behind The Clock Tower: Stories From The Set | Digital Edition', 'Great Scott! 40 Quotes For 40 Years | Digital Edition', 'Time Imagination Destiny: The Philosophical And Cultural Legacy Of Bttf | Digital Edition', 'Instant Download'],
    cta: 'GET COMPLETE DIGITAL EDITION',
    purchaseUrl: 'https://legendaryreads.com/cart/42231842701394:1?checkout&fbclid={{fbclid}}',
    popular: true,
    isHero: true,
    bestValue: true,
    borderColor: 'border-transparent bg-gradient-to-br from-emerald-600/20 via-teal-600/20 to-green-700/20',
    hoverBorderColor: 'hover:from-emerald-600/35 hover:via-teal-600/35 hover:to-green-700/35',
    shadow: 'hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]',
    ctaClass: 'bg-gradient-to-br from-emerald-600/90 via-teal-600/90 to-green-700/90 hover:from-emerald-700 hover:via-teal-700 hover:to-green-800 text-white shadow-[0_0_15px_rgba(16,185,129,0.5)] hover:shadow-[0_0_25px_rgba(16,185,129,0.7)] border border-emerald-400/30 font-semibold uppercase text-base tracking-wide',
    badge: {
      text: 'THE COMPLETE DIGITAL EXPERIENCE',
      tooltip: 'Recommended Choice',
      icon: null,
      gradient: 'bg-gradient-to-br from-emerald-600/80 via-teal-600/80 to-green-700/80',
      textColor: 'text-white',
      glow: 'shadow-[0_0_15px_rgba(16,185,129,0.5)]'
    },
    shopPayAmount: '$15',
    shippingBadge: {
      text: '⚡ INSTANT DOWNLOAD',
      classes: 'bg-gradient-to-br from-emerald-600/80 via-teal-600/80 to-green-700/80 text-white font-semibold px-6 py-3 rounded-lg shadow-[0_0_15px_rgba(16,185,129,0.5)] border border-emerald-400/30'
    },
    desktopOrder: 2
  }, {
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png',
    name: 'Premium Hardcover Edition Bundle',
    emoji: '⭐',
    originalPrice: '$127.97',
    currentPrice: '$94.97',
    savings: '$33.00',
    features: ['Back In Time - 40th Anniversary Collectors Edition (1985-2025) | Premium Hardcover Edition + Digital Edition', 'Behind The Clock Tower: Stories From The Set | Digital Edition', 'Great Scott! 40 Quotes For 40 Years | Digital Edition', 'Time Imagination Destiny: The Philosophical And Cultural Legacy Of Bttf | Digital Edition', 'Delivery within 12 days'],
    cta: 'GET PREMIUM HARDCOVER EDITION BUNDLE',
    purchaseUrl: 'https://legendaryreads.com/cart/42285346947154:1?checkout&fbclid={{fbclid}}',
    popular: false,
    borderColor: 'border-transparent bg-gradient-to-br from-rose-600/20 via-red-700/20 to-red-900/20',
    hoverBorderColor: 'hover:from-rose-600/35 hover:via-red-700/35 hover:to-red-900/35',
    shadow: 'hover:shadow-[0_0_25px_rgba(225,29,72,0.4)]',
    ctaClass: 'bg-gradient-to-br from-rose-600/90 via-red-700/90 to-red-900/90 hover:from-rose-700 hover:via-red-800 hover:to-red-950 text-white shadow-[0_0_15px_rgba(225,29,72,0.5)] hover:shadow-[0_0_25px_rgba(225,29,72,0.7)] border border-rose-400/30 font-semibold uppercase text-base tracking-wide',
    badge: {
      text: 'FOR COLLECTORS WHO WANT IT ON THEIR SHELF',
      tooltip: 'Physical Edition',
      icon: null,
      gradient: 'bg-gradient-to-br from-rose-600/80 via-red-700/80 to-red-900/80',
      textColor: 'text-white',
      glow: 'shadow-[0_0_15px_rgba(225,29,72,0.5)]'
    },
    shopPayAmount: '$23.75',
    shippingBadge: {
      text: '📦 FREE SHIPPING',
      classes: 'bg-gradient-to-br from-rose-600/80 via-red-700/80 to-red-900/80 text-white font-semibold px-6 py-3 rounded-lg shadow-[0_0_15px_rgba(225,29,72,0.5)] border border-rose-400/30'
    },
    desktopOrder: 3
  }, {
    image: collectorBundleImages[collectorBundleImageIndex],
    name: "Time Traveler's Collector Bundle",
    emoji: '🚀',
    originalPrice: '$419.97',
    currentPrice: '$279.97',
    savings: '$140.00',
    features: ['Back In Time - 40th Anniversary Collectors Edition (1985-2025) | Premium Hardcover Edition + Digital Edition', 'Behind The Clock Tower: Stories From The Set | Paperback Edition + Digital Edition', 'Great Scott! 40 Quotes For 40 Years | Paperback Edition + Digital Edition', 'Time Imagination Destiny: The Philosophical And Cultural Legacy Of Bttf | Paperback Edition + Digital Edition', 'Back In Time Audiobook Experience', 'Hill Valley 1985, Limited Vintage Trucker Hat', "Flux Energy Core Collector's Framed Print | 10×10 Limited Edition", 'Time Machine Heritage Tote | Back In Time Edition', '40 Years Travelling Through Time Commemorative Gloss Black Mug', 'Fast Delivery'],
    cta: "GET TIME TRAVELER'S COLLECTOR BUNDLE",
    purchaseUrl: 'https://legendaryreads.com/cart/42285352288338:1?checkout&fbclid={{fbclid}}',
    popular: false,
    borderColor: 'border-transparent bg-gradient-to-br from-purple-500/20 via-violet-600/20 to-indigo-700/20',
    hoverBorderColor: 'hover:from-purple-500/35 hover:via-violet-600/35 hover:to-indigo-700/35',
    shadow: 'hover:shadow-[0_0_25px_rgba(139,92,246,0.4)]',
    ctaClass: 'bg-gradient-to-br from-purple-500/90 via-violet-600/90 to-indigo-700/90 hover:from-purple-600 hover:via-violet-700 hover:to-indigo-800 text-white shadow-[0_0_15px_rgba(139,92,246,0.5)] hover:shadow-[0_0_25px_rgba(139,92,246,0.7)] border border-purple-400/30 font-semibold uppercase text-base tracking-wide',
    badge: {
      text: 'THE ULTIMATE 40TH ANNIVERSARY COLLECTION',
      tooltip: 'Complete Collection',
      icon: null,
      gradient: 'bg-gradient-to-br from-purple-500/80 via-violet-600/80 to-indigo-700/80',
      textColor: 'text-white',
      glow: 'shadow-[0_0_15px_rgba(139,92,246,0.5)]'
    },
    hasCarousel: true,
    shopPayAmount: '$70',
    shippingBadge: {
      text: '🚀 FREE + FAST DELIVERY',
      classes: 'bg-gradient-to-br from-purple-500/80 via-violet-600/80 to-indigo-700/80 text-white font-semibold px-6 py-3 rounded-lg shadow-[0_0_15px_rgba(139,92,246,0.5)] border border-purple-400/30'
    },
    desktopOrder: 4
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/ewSrEYsf3d6aAxsfRJbJ5B/BACK_IN_TIME_-_DIGITAL11v2.png',
    name: 'Digital Edition',
    emoji: '📱',
    originalPrice: '$55.97',
    currentPrice: '$39.97',
    savings: '$16.00',
    features: ['Back In Time - 40th Anniversary Collectors Edition (1985-2025) | Digital Edition', 'Instant Download'],
    cta: 'Get Digital Edition',
    purchaseUrl: 'https://legendaryreads.com/cart/42185022439506:1?checkout&fbclid={{fbclid}}',
    popular: false,
    isDeemphasized: true,
    borderColor: 'border-gray-700/40 bg-gray-900/20',
    hoverBorderColor: 'hover:border-gray-600/50',
    shadow: 'hover:shadow-sm',
    ctaClass: 'bg-gray-700 hover:bg-gray-600 text-gray-200 font-normal uppercase text-base tracking-wide',
    badge: {
      text: 'Basic Access',
      tooltip: 'Digital Only',
      icon: null,
      gradient: 'bg-gray-700/60',
      textColor: 'text-gray-300',
      glow: ''
    },
    shippingBadge: {
      text: 'Instant Access',
      classes: 'bg-gray-700/50 text-gray-300 font-semibold px-6 py-3 rounded-lg border border-gray-600/30'
    },
    desktopOrder: 1
  }];
  // Desktop order: Digital Edition -> Complete Digital -> Premium -> Collector Bundle
  const editionsDesktop = [...editionsMobile].sort((a, b) => a.desktopOrder - b.desktopOrder);
  return <section ref={sectionRef} className="py-4 md:py-6 bg-bttf-bg border-t border-b border-bttf-subtext/20 scroll-mt-20" id="pricing-top" data-id="element-338">
      <div className="container mx-auto px-4" data-id="element-339">
        <div className="text-center mb-4 md:mb-10" data-id="element-340">
          <h2 className="text-3xl md:text-4xl font-bold font-future tracking-wide bg-gradient-main text-transparent bg-clip-text mb-2 pb-2 w-[80%] md:w-auto mx-auto leading-tight" data-id="element-341">
            Special 40th Anniversary Collector's Offer
          </h2>
          <div className="w-20 h-1 bg-bttf-primary mx-auto mb-3" data-id="element-342"></div>
          <div className="mb-6 md:mb-6 flex justify-center" data-id="element-343">
            <CopiesAvailableCounter data-id="element-344" />
          </div>
        </div>

        {/* Mobile: original order */}
        <div className="grid grid-cols-1 md:hidden gap-12 max-w-7xl mx-auto items-start" data-id="element-345">
          {editionsMobile.map((edition, index) => <a key={index} href={buildCheckoutUrl(edition.purchaseUrl)} onClick={() => fireTrackingEvents(edition.name, edition.currentPrice)} className={`cta block no-underline bg-bttf-bg/80 rounded-lg overflow-visible border-2 ${edition.borderColor} ${edition.hoverBorderColor} backdrop-blur-sm ${edition.shadow} transition-all duration-300 relative pt-14 cursor-pointer ${edition.isHero ? 'z-20' : edition.isDeemphasized ? 'z-0 opacity-80 hover:opacity-90' : 'z-10'}`} style={{
          textDecoration: 'none',
          color: 'inherit'
        }} data-id="element-346">
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none w-full px-2" data-id="element-347">
                <div className="relative group flex justify-center" data-id="element-348">
                  <div className={`plan-badge flex items-center gap-1.5 rounded-full ${edition.badge.gradient} ${edition.badge.textColor} ${edition.badge.glow} ${edition.isDeemphasized ? 'font-normal' : 'font-medium'} ${edition.isDeemphasized ? 'normal-case' : 'uppercase'} tracking-wider transition-all duration-150 border ${edition.isDeemphasized ? 'border-gray-600/30' : 'border-white/20'} text-center px-3 py-2 text-xs max-w-[85%]`} style={{
                letterSpacing: edition.isDeemphasized ? '0' : '0.02em'
              }} aria-label={edition.badge.text} data-id="element-349">
                    {edition.badge.icon}
                    <span className="leading-tight" data-id="element-350">{edition.badge.text}</span>
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-351">
                {/* Image with optional carousel */}
                <div className="flex justify-center mb-6 relative" data-id="element-352">
                  {edition.hasCarousel ? <div className="relative w-full md:max-w-[224px] group" onClick={stopProp} data-id="element-353">
                      <div className="w-full rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity duration-300" style={{
                  aspectRatio: '1/1'
                }} onClick={e => {
                  e.stopPropagation();
                  handleImageClick(collectorBundleImageIndex);
                }} data-id="element-354">
                        <img src={edition.image} alt={`${edition.name} - Item ${collectorBundleImageIndex + 1}`} className="w-full h-full object-cover transition-opacity duration-300" width="224" height="224" loading="lazy" decoding="async" data-id="element-355" />
                      </div>
                      <button onClick={e => {
                  e.stopPropagation();
                  prevCollectorImage();
                }} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-bttf-primary/90 hover:bg-bttf-primary text-bttf-text p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10" aria-label="Previous item" data-id="element-356">
                        <ChevronLeftIcon className="h-4 w-4" data-id="element-357" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  nextCollectorImage();
                }} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 bg-bttf-primary/90 hover:bg-bttf-primary text-bttf-text p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10" aria-label="Next item" data-id="element-358">
                        <ChevronRightIcon className="h-4 w-4" data-id="element-359" />
                      </button>
                      <div className="flex justify-center gap-1.5 mt-3" data-id="element-360">
                        {collectorBundleImages.map((_, imgIndex) => <button key={imgIndex} onClick={e => {
                    e.stopPropagation();
                    setCollectorBundleImageIndex(imgIndex);
                  }} className={`w-2 h-2 rounded-full transition-all duration-300 ${imgIndex === collectorBundleImageIndex ? 'bg-bttf-primary shadow-neon-orange w-4' : 'bg-bttf-subtext/50 hover:bg-bttf-subtext'}`} aria-label={`Go to item ${imgIndex + 1}`} data-id="element-361" />)}
                      </div>
                    </div> : <div className="w-full md:max-w-[224px]" onClick={stopProp} data-id="element-362">
                      <div className="w-full rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity duration-300" style={{
                  aspectRatio: '1/1'
                }} onClick={e => {
                  e.stopPropagation();
                  handleMainImageClick(edition.image);
                }} data-id="element-363">
                        <img src={edition.image} alt={edition.name} className="w-full h-full object-cover" width="224" height="224" loading="lazy" decoding="async" data-id="element-364" />
                      </div>
                    </div>}
                </div>
                <div className="text-center mb-6" data-id="element-365">
                  {(edition as any).bestValue && <div className="inline-flex items-center gap-1 text-amber-300 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-2 border border-amber-400/50 animate-best-value-pulse" data-id="element-366">
                      ⭐ Best Value
                    </div>}
                  <h3 className={`text-lg font-medium ${edition.isDeemphasized ? 'text-gray-400' : 'text-gray-200'} mb-1 tracking-wide`} data-id="element-367">
                    {edition.name}
                  </h3>
                  <div className="mt-2" data-id="element-368">
                    <span className="text-gray-400 line-through text-lg" data-id="element-369">
                      {edition.originalPrice}
                    </span>
                    <span className="text-bttf-accent font-bold ml-2 text-2xl" data-id="element-370">
                      {edition.currentPrice}
                    </span>
                  </div>
                  {(edition as any).savings && <div className="mt-1.5 inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-371">
                      You Save {(edition as any).savings}
                    </div>}
                  {edition.shopPayAmount && <div className="mt-3 px-3 py-2 bg-black/30 rounded-lg border border-white/10" data-id="element-372">
                      <div className="flex items-center justify-center gap-2 mb-1" data-id="element-373">
                        <span className="text-gray-300 text-xs" data-id="element-374">
                          or 4 payments of
                        </span>
                        <span className="text-bttf-accent font-bold text-sm" data-id="element-375">
                          {edition.shopPayAmount}
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-1.5" data-id="element-376">
                        <span className="text-gray-400 text-xs" data-id="element-377">with</span>
                        <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-378" />
                      </div>
                      <p className="text-gray-400 text-[10px] mt-1" data-id="element-379">
                        Biweekly interest-free payments
                      </p>
                    </div>}
                  {edition.shippingBadge && <div className="mt-4" data-id="element-380">
                      <div className={`inline-block ${edition.shippingBadge.classes}`} data-id="element-381">
                        <span className="text-base tracking-wide" data-id="element-382">
                          {edition.shippingBadge.text}
                        </span>
                      </div>
                    </div>}
                </div>

                {/* Features — first 2 always visible, rest collapsible */}
                <ul className="mb-3" data-id="element-383">
                  {edition.features.slice(0, 2).map((feature, i) => <li key={i} className={`flex items-start mb-2 ${edition.isDeemphasized ? 'text-gray-300' : 'text-gray-200'} text-sm`} data-id="element-384">
                      <CheckIcon className={`${edition.isDeemphasized ? 'text-gray-500' : 'text-bttf-primary'} mr-2 mt-0.5 flex-shrink-0 h-4 w-4`} data-id="element-385" />
                      <span data-id="element-386">{feature}</span>
                    </li>)}
                  {expandedCards[index] && edition.features.slice(2).map((feature, i) => <li key={i + 2} className={`flex items-start mb-2 ${edition.isDeemphasized ? 'text-gray-300' : 'text-gray-200'} text-sm`} data-id="element-387">
                        <CheckIcon className={`${edition.isDeemphasized ? 'text-gray-500' : 'text-bttf-primary'} mr-2 mt-0.5 flex-shrink-0 h-4 w-4`} data-id="element-388" />
                        <span data-id="element-389">{feature}</span>
                      </li>)}
                </ul>

                {/* Expand toggle — only if more than 2 features */}
                {edition.features.length > 2 && <button type="button" onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setExpandedCards(prev => ({
                ...prev,
                [index]: !prev[index]
              }));
            }} className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg mb-4 text-sm font-semibold transition-all duration-200 border ${expandedCards[index] ? 'bg-bttf-primary/10 border-bttf-primary/40 text-bttf-primary' : 'bg-white/5 border-white/20 text-white active:bg-white/10'}`} data-id="element-390">
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${expandedCards[index] ? 'rotate-180' : ''}`} data-id="element-391" />
                    {expandedCards[index] ? 'Show less' : "Show me what's inside"}
                  </button>}

                <span className={`block text-center py-3 rounded-md transition-all ${edition.ctaClass} transform hover:scale-105`} data-id="element-392">
                  {edition.cta}
                </span>
              </div>
            </a>)}
        </div>

        {/* Desktop: reordered with Digital Edition first */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-6 max-w-7xl mx-auto items-start" data-id="element-393">
          {editionsDesktop.map((edition, index) => <a key={index} href={buildCheckoutUrl(edition.purchaseUrl)} onClick={() => fireTrackingEvents(edition.name, edition.currentPrice)} className={`cta block no-underline bg-bttf-bg/80 rounded-lg overflow-visible border-2 ${edition.borderColor} ${edition.hoverBorderColor} backdrop-blur-sm ${edition.shadow} transition-all duration-300 relative pt-14 md:pt-10 cursor-pointer ${edition.isHero ? 'z-20 hover:scale-[1.02]' : edition.isDeemphasized ? 'z-0 opacity-80 hover:opacity-90' : 'z-10 hover:scale-[1.02]'}`} style={{
          textDecoration: 'none',
          color: 'inherit'
        }} data-id="element-394">
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none w-full px-2" data-id="element-395">
                <div className="relative group flex justify-center" data-id="element-396">
                  <div className={`plan-badge flex items-center gap-1.5 rounded-full ${edition.badge.gradient} ${edition.badge.textColor} ${edition.badge.glow} ${edition.isDeemphasized ? 'font-normal' : 'font-medium'} ${edition.isDeemphasized ? 'normal-case' : 'uppercase'} tracking-wider transition-all duration-150 border ${edition.isDeemphasized ? 'border-gray-600/30' : 'border-white/20'} text-center px-3 py-2 text-xs max-w-[85%]`} style={{
                letterSpacing: edition.isDeemphasized ? '0' : '0.02em'
              }} aria-label={edition.badge.text} data-id="element-397">
                    {edition.badge.icon}
                    <span className="leading-tight" data-id="element-398">{edition.badge.text}</span>
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-399">
                {/* Image with optional carousel */}
                <div className="flex justify-center mb-6 relative" data-id="element-400">
                  {edition.hasCarousel ? <div className="relative w-full md:max-w-[224px] group" onClick={stopProp} data-id="element-401">
                      <div className="w-full rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity duration-300" style={{
                  aspectRatio: '1/1'
                }} onClick={e => {
                  e.stopPropagation();
                  handleImageClick(collectorBundleImageIndex);
                }} data-id="element-402">
                        <img src={edition.image} alt={`${edition.name} - Item ${collectorBundleImageIndex + 1}`} className="w-full h-full object-cover transition-opacity duration-300" width="224" height="224" loading="lazy" decoding="async" data-id="element-403" />
                      </div>
                      {/* Carousel Navigation Buttons */}
                      <button onClick={e => {
                  e.stopPropagation();
                  prevCollectorImage();
                }} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-bttf-primary/90 hover:bg-bttf-primary text-bttf-text p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10" aria-label="Previous item" data-id="element-404">
                        <ChevronLeftIcon className="h-4 w-4" data-id="element-405" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  nextCollectorImage();
                }} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 bg-bttf-primary/90 hover:bg-bttf-primary text-bttf-text p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10" aria-label="Next item" data-id="element-406">
                        <ChevronRightIcon className="h-4 w-4" data-id="element-407" />
                      </button>
                      {/* Carousel Dots */}
                      <div className="flex justify-center gap-1.5 mt-3" data-id="element-408">
                        {collectorBundleImages.map((_, imgIndex) => <button key={imgIndex} onClick={e => {
                    e.stopPropagation();
                    setCollectorBundleImageIndex(imgIndex);
                  }} className={`w-2 h-2 rounded-full transition-all duration-300 ${imgIndex === collectorBundleImageIndex ? 'bg-bttf-primary shadow-neon-orange w-4' : 'bg-bttf-subtext/50 hover:bg-bttf-subtext'}`} aria-label={`Go to item ${imgIndex + 1}`} data-id="element-409" />)}
                      </div>
                    </div> : <div className="w-full md:max-w-[224px]" onClick={stopProp} data-id="element-410">
                      <div className="w-full rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity duration-300" style={{
                  aspectRatio: '1/1'
                }} onClick={e => {
                  e.stopPropagation();
                  handleMainImageClick(edition.image);
                }} data-id="element-411">
                        <img src={edition.image} alt={edition.name} className="w-full h-full object-cover" width="224" height="224" loading="lazy" decoding="async" data-id="element-412" />
                      </div>
                    </div>}
                </div>
                <div className="text-center mb-6" data-id="element-413">
                  {(edition as any).bestValue && <div className="inline-flex items-center gap-1 text-amber-300 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-2 border border-amber-400/50 animate-best-value-pulse" data-id="element-414">
                      ⭐ Best Value
                    </div>}
                  <h3 className={`text-lg font-medium ${edition.isDeemphasized ? 'text-gray-400' : 'text-gray-200'} mb-1 tracking-wide`} data-id="element-415">
                    {edition.name}
                  </h3>
                  <div className="mt-2" data-id="element-416">
                    <span className="text-gray-400 line-through text-lg" data-id="element-417">
                      {edition.originalPrice}
                    </span>
                    <span className="text-bttf-accent font-bold ml-2 text-2xl" data-id="element-418">
                      {edition.currentPrice}
                    </span>
                  </div>
                  {(edition as any).savings && <div className="mt-1.5 inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-419">
                      You Save {(edition as any).savings}
                    </div>}
                  {edition.shopPayAmount && <div className="mt-3 px-3 py-2 bg-black/30 rounded-lg border border-white/10" data-id="element-420">
                      <div className="flex items-center justify-center gap-2 mb-1" data-id="element-421">
                        <span className="text-gray-300 text-xs" data-id="element-422">
                          or 4 payments of
                        </span>
                        <span className="text-bttf-accent font-bold text-sm" data-id="element-423">
                          {edition.shopPayAmount}
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-1.5" data-id="element-424">
                        <span className="text-gray-400 text-xs" data-id="element-425">with</span>
                        <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-426" />
                      </div>
                      <p className="text-gray-400 text-[10px] mt-1" data-id="element-427">
                        Biweekly interest-free payments
                      </p>
                    </div>}
                  {edition.shippingBadge && <div className="mt-4" data-id="element-428">
                      <div className={`inline-block ${edition.shippingBadge.classes}`} data-id="element-429">
                        <span className="text-base tracking-wide" data-id="element-430">
                          {edition.shippingBadge.text}
                        </span>
                      </div>
                    </div>}
                </div>
                <ul className="mb-6" data-id="element-431">
                  {edition.features.map((feature, i) => <li key={i} className={`flex items-start mb-2 ${edition.isDeemphasized ? 'text-gray-300' : 'text-gray-200'} text-sm`} data-id="element-432">
                      <CheckIcon className={`${edition.isDeemphasized ? 'text-gray-500' : 'text-bttf-primary'} mr-2 mt-0.5 flex-shrink-0 h-4 w-4`} data-id="element-433" />
                      <span data-id="element-434">{feature}</span>
                    </li>)}
                </ul>
                {/* CTA visual button — NOT a separate .cta element */}
                <span className={`block text-center py-3 rounded-md transition-all ${edition.ctaClass} transform hover:scale-105`} data-id="element-435">
                  {edition.cta}
                </span>
              </div>
            </a>)}
        </div>
      </div>
      {/* Image Enlargement Modal */}
      {enlargedImage && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={closeEnlargedImage} data-id="element-436">
          <button onClick={closeEnlargedImage} className="absolute top-4 right-4 text-bttf-text hover:text-bttf-primary transition-colors bg-bttf-bg/50 rounded-full p-2 hover:bg-bttf-bg/80" aria-label="Close enlarged image" data-id="element-437">
            <XIcon className="h-8 w-8" data-id="element-438" />
          </button>
          <div className="relative max-w-4xl max-h-[90vh] w-full" data-id="element-439">
            <img src={enlargedImage} alt="Collector Bundle Item" className="w-full h-full object-contain rounded-lg" onClick={e => e.stopPropagation()} loading="lazy" decoding="async" data-id="element-440" />
          </div>
        </div>}
    </section>;
};
const StarIcon = ({
  className
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-441">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" data-id="element-442"></polygon>
  </svg>;